export interface OrderSpecifiedPaymentTotal {
  productAmount: number;
  shippingAmount: number;
  discountAmount: number;
  couponAmount: number;
  totalAmount: number;
}

const DISCOUNT_FIELDS = [
  "membership_discount_amount",
  "shipping_fee_discount_amount",
  "set_product_discount_amount",
  "app_discount_amount",
  "market_other_discount_amount",
];

const COUPON_FIELDS = ["coupon_discount_price", "coupon_shipping_fee_amount"];

function toAmount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return 0;
  const amount = Number(value.replace(/,/g, "").trim());
  return Number.isFinite(amount) ? amount : 0;
}

function sumFields(source: unknown, fields: string[]): number {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return 0;
  }
  const record = source as Record<string, unknown>;
  return fields.reduce((sum, field) => sum + toAmount(record[field]), 0);
}

function sumItemProductAmounts(items: unknown): number {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => {
    if (!item || typeof item !== "object") return sum;
    const record = item as Record<string, unknown>;
    const quantity = toAmount(record.quantity) || 1;
    return (
      sum +
      (toAmount(record.product_price) + toAmount(record.option_price)) *
        quantity
    );
  }, 0);
}

function sumItemAdditionalDiscounts(items: unknown): number {
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (sum, item) =>
      sum +
      toAmount((item as Record<string, unknown>)?.additional_discount_price),
    0
  );
}

function summarizeOrder(order: any): OrderSpecifiedPaymentTotal {
  const initialAmount = order?.initial_order_amount;
  const productAmount = sumItemProductAmounts(order?.items);
  const shippingAmount = toAmount(initialAmount?.shipping_fee);
  const discountAmount =
    sumFields(initialAmount, DISCOUNT_FIELDS) +
    sumItemAdditionalDiscounts(order?.items);
  const couponAmount = sumFields(initialAmount, COUPON_FIELDS);

  return {
    productAmount,
    shippingAmount,
    discountAmount,
    couponAmount,
    totalAmount: productAmount + shippingAmount - discountAmount - couponAmount,
  };
}

export function summarizeOrderSpecifiedPaymentTotal(
  orders: any[]
): OrderSpecifiedPaymentTotal {
  return (Array.isArray(orders) ? orders : []).reduce(
    (summary, order) => {
      const item = summarizeOrder(order);
      summary.productAmount += item.productAmount;
      summary.shippingAmount += item.shippingAmount;
      summary.discountAmount += item.discountAmount;
      summary.couponAmount += item.couponAmount;
      summary.totalAmount += item.totalAmount;
      return summary;
    },
    {
      productAmount: 0,
      shippingAmount: 0,
      discountAmount: 0,
      couponAmount: 0,
      totalAmount: 0,
    }
  );
}
